import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import { useRoSContext } from '@components/Presentation/context/RoSContext';
import Switch from '@molecules/Switch';
import SettingBlock from './units/SettingBlock';
import ShowCommentsOverlay from './units/ShowCommentsOverlay';
import RoSColorPicker from './units/RoSColorPicker';
import ApplyAllOrReset from './units/ApplyAllOrReset';
import Input from '@atoms/Input';
import { panelBtnEnum } from '@components/Presentation/constants';
import Tooltip from '@components/Presentation/atoms/Tooltip';
import MaxLengthCounter from '@components/Presentation/atoms/MaxLengthCounter';
import { calculateRemainingChars } from '@components/Presentation/helpers';
import useFocusRef from '@components/Presentation/hooks/useFocusRef';
import SlideHeading from './units/SlideHeading';
import BackgroundImage from './units/BackgroundImage';
import { computeLuminance } from '@lib/interactionHelper';
import { SelectItem, Select } from '@atoms/new/select';
import { StarBorder, SentimentVerySatisfied, Grade } from '@mui/icons-material';

import { Tabs, Tab, } from '@nextui-org/react';
import { Radio, RadioGroup } from '@atoms/new/RadioGroup';
import ShowProfilePictures from './units/ShowProfilePictures';
import TextLimiterInput from '@molecules/TextLimiterInput';

export const EMOJI_ICONS = {
  5: {
    1: '😩',
    2: '😔',
    3: '😐',
    4: '🙂',
    5: '😊',
  },
  10: {
    1: '😩',
    2: '😔',
    3: '😐',
    4: '🙂',
    5: '😊',
    6: '😄',
    7: '😁',
    8: '😆',
    9: '😅',
    10: '😍',
  }
};

function RatingPollsSettings() {
  const { selectedSlide, selectedPanelBtn, disableAutoFocus } =
    useRoSContext();
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const ref = useRef<any>(null);
  const inputRef = useFocusRef<HTMLInputElement>([selectedSlide.type], disableAutoFocus);

  useEffect(() => {
    if (selectedSlide?.options?.length > 2) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedSlide?.options?.length]);

  return (
    <>
      {selectedPanelBtn === panelBtnEnum.content && (
        <>
          {/* <div className="mt-6 mb-2 px-3">
            <Button
              fullWidth
              kind="secondary"
              size='sm'
              onClick={() => setShowGenerateWithAIModal(true)}
              startContent={<AutoAwesomeIcon fontSize="small" />}
            >
            Generate with AI
            </Button>
          </div> */}

          <div className='border-1 rounded-md bg-grey-50 dark:bg-grey-800/60 dark:border-grey-800 p-2.5 mx-3 my-5 '>
            <SettingBlock
              title="Heading"
              hasBorderTop={false}
              tooltip={
                <p>
                  Add a prompt—such as a question <br /> or instruction—for your
                  audience.
                </p>
              }
              titleMargin="sm"
              hasParentBg
            >
              <div>
                <Input
                  ref={inputRef}
                  placeholder="Type a prompt or question..."
                  value={selectedSlide.title}
                  onChange={selectedSlide.handler.updateTitle}
                  size="lg"
                  maxLength={255}
                  handleFocus={(v) => {
                    setIsTitleFocused(v);
                    if (v) {
                      inputRef.current?.select();
                    }
                  }}
                  addOnRight={
                    isTitleFocused ? (
                      <MaxLengthCounter
                        value={calculateRemainingChars(selectedSlide.title)}
                      />
                    ) : null
                  }
                />
                {calculateRemainingChars(selectedSlide.title) === 0 && (
                  <p className="text-sm text-alert mt-1">
                    You have reached the maximum length
                  </p>
                )}
              </div>
            </SettingBlock>

            <span className="block mt-4"></span>

          </div>
          <SettingBlock
            title="Scale adjustment"
          >
            <div className='flex justify-between items-center'>
              <p className='text-sm font-medium'>Choose a rating scale</p>
              <div className='w-1/3'>
                <Select selectedKeys={new Set([selectedSlide?.setting?.ratingPollScale?.toString() || '5'])} onSelectionChange={(key) => {
                  if (!key.currentKey) return;
                  selectedSlide.handler.updateSetting('ratingPollScale', key.currentKey);
                  selectedSlide.handler.addBulkOptions(parseInt(key.currentKey));
                }}>
                  <SelectItem key={5} value={5}>5</SelectItem>
                  <SelectItem key={10} value={10}>10</SelectItem>
                </Select>
              </div>

            </div>
          </SettingBlock>

          <SettingBlock

          >
            <div className=' '>
              <Tabs
                size='lg'
                classNames={{
                  tabList: 'bg-grey-100 dark:bg-grey-800 rounded-lg p-0.5',
                  tabContent: 'text-sm font-semibold group-data-[selected=true]:text-primary-500 ',
                  cursor: 'shadow-none rounded-md',

                }}

                selectedKey={selectedSlide?.setting?.scoreLabelType}
                className="self-center "

                onSelectionChange={(key) => {
                  selectedSlide.handler.updateSetting('scoreLabelType', key);
                }}
              >

                <Tab key="LABELS" title="Labels">
                  <div className='mt-6 flex  gap-2 items-center justify-between'>
                    <p className='text-sm font-normal'>Label (for 1)</p>
                    <div className='w-1/2'>
                      <TextLimiterInput
                        value={selectedSlide?.setting?.lowScoreLabel || ''}
                        setValue={(value) => selectedSlide.handler.updateSetting('lowScoreLabel', value)}
                        limit={50}
                        placeholder='Enter label' />

                    </div>
                  </div>
                  <div className='mt-6 flex  gap-2 items-center justify-between'>
                    <p className='text-sm font-normal'>Label (for {selectedSlide?.setting?.ratingPollScale || '5'})</p>
                    <div className='w-1/2'>
                      <TextLimiterInput
                        value={selectedSlide?.setting?.highScoreLabel || ''}
                        setValue={(value) => selectedSlide.handler.updateSetting('highScoreLabel', value)}
                        limit={50}
                        placeholder='Enter label' />
                    </div>
                  </div>
                </Tab>
                <Tab key="ICONS" title="Icons">
                  <div className='mt-6'>
                    <RadioGroup value={selectedSlide?.setting?.iconType} defaultSelectedKeys={['STAR']} onChange={(key: 'STAR' | 'EMOJI') => {
                      selectedSlide.handler.updateSetting('iconType', key);
                    }}>
                      <Radio key='STAR' value='STAR' >
                        <StarBorder className={selectedSlide?.setting?.iconType === 'STAR' ? 'text-primary-500' : 'text-gray-400'} />
                      </Radio>
                      <Radio key='EMOJI' value='EMOJI'>
                        <SentimentVerySatisfied className={selectedSlide?.setting?.iconType === 'EMOJI' ? 'text-primary-500' : 'text-gray-400'} />
                      </Radio>
                    </RadioGroup>
                  </div>

                  {selectedSlide.setting.iconType === 'STAR' ?
                    <div className='mt-6 flex   items-center justify-between'>
                      {Array.from({ length: parseInt(selectedSlide.setting.ratingPollScale || '1') }).map((_, index) => (
                        <Grade key={index} className='text-yellow-600' />
                      ))}

                    </div>
                    : null}

                  {selectedSlide.setting.iconType === 'EMOJI' ?
                    <div className='mt-6 flex   items-center justify-between'>
                      {Array.from({ length: parseInt(selectedSlide.setting.ratingPollScale || '1') }).map((_, index) => {
                        const scale = selectedSlide.setting.ratingPollScale as '5' | '10';
                        return (
                          <span key={`emoji-${index}`}>{EMOJI_ICONS[scale][(index + 1) as keyof typeof EMOJI_ICONS[typeof scale]]}</span>
                        );
                      })}
                    </div>
                    : null}
                </Tab>
              </Tabs>

            </div>
          </SettingBlock>

          <SettingBlock title="Audience responses" hasBorderTop>
            <ShowCommentsOverlay />
            <ShowProfilePictures settingKey='ratingPollShowProfilePictures' />
            <div className='mt-6 flex  gap-2 items-center justify-between'></div>
          </SettingBlock>
          <SettingBlock title="Result Layout">
            <div className="flex">
              <div className="flex-1 text-sm">
                Show as percentages (%) &nbsp;
                <Tooltip content="When enabled, the percentage of votes is shown. Else, the number of votes are shown.">
                  <HelpRoundedIcon
                    fontSize="small"
                    color="disabled"
                    className='dark:text-grey-400'
                  />
                </Tooltip>
              </div>
              <Switch
                checked={selectedSlide.setting.DisplayStyle === 'percentage'}
                onChange={(e) =>
                  selectedSlide.handler.updateSetting(
                    'DisplayStyle',
                    e.target.checked
                  )
                }
                onClick={() => { }}
              />
            </div>
          </SettingBlock>
        </>
      )}
      {selectedPanelBtn === panelBtnEnum.design && (
        <>
          <SettingBlock title="Visualization colors"
            hasBorderTop={false}
            addPadding>
            <RoSColorPicker
              disableGradient
              color={selectedSlide.color.ratingPollsChartColor}
              onChange={(color) =>
                selectedSlide.handler.updateColor('ratingPollsChartColor', color)
              }
            >
              Bar color
            </RoSColorPicker>
            <RoSColorPicker
              disableGradient
              color={selectedSlide.color.ratingPollsLabelColor}
              onChange={(color) =>
                selectedSlide.handler.updateColor(
                  'ratingPollsLabelColor',
                  color
                )
              }
            >
              Text color
            </RoSColorPicker>
          </SettingBlock>
          <SettingBlock title="Background">
            <RoSColorPicker
              color={selectedSlide.color.ratingPollsBgColor}
              onChange={(color) => {
                selectedSlide.handler.updateColor('ratingPollsBgColor', color);
                const { isBgLight } = computeLuminance(color);
                const newEvent = isBgLight ? '#111111' : '#ffffff';
                selectedSlide.handler.updateColor('ratingPollsHeadingColor', newEvent);
              }
              }
            >
              Background color
            </RoSColorPicker>
            <BackgroundImage />
          </SettingBlock>
          {/* <RoSFontFamily /> */}
          <SlideHeading
            color={selectedSlide.color.ratingPollsHeadingColor}
            updateColor={(color) =>
              selectedSlide.handler.updateColor('ratingPollsHeadingColor', color)
            } />
          <ApplyAllOrReset />
        </>
      )}
    </>
  );
}

export default RatingPollsSettings;
